import * as React from "react";
import useViewer from "../../hooks/useViewer";
import { InsertGroupForm } from "./InsertGroupForm";
import useTranslations from "../../hooks/useTranslations";
import useNotification from "../../hooks/useNotification";

import { useInsertGroup, GroupFragment as Group, GetGroupsDocument } from '../../graphql/generated/definitions';

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer
} from '@chakra-ui/react';
interface InsertGroupDialogProps {
  open: boolean;
  group?: Group;
  onClose: () => void;
  defaultName?: string;
}

export const InsertGroupDialog: React.FC<InsertGroupDialogProps> = ({
  group,
  open,
  onClose,
  defaultName,
}) => {
  const [insertGroup, { loading }] = useInsertGroup({
    refetchQueries: [
      { query: GetGroupsDocument, variables: { q: defaultName, sortBy: "name" } },
      { query: GetGroupsDocument },
    ],
  });

  const { notifySuccess } = useNotification();
  const { company } = useViewer();
  const t = useTranslations();

  const submit = React.useCallback((data: Group) => {
    insertGroup({
      variables: {
        input: {
          ...data,
          companyId: company.id,
        },
      },
    })
      .then(() => {
        const action = data.id ? "update" : "create";

        notifySuccess({
          title: t(
            `groups.notification.success.${action}.title` as const
          ) as string,
          description: t(
            `groups.notification.success.${action}.body` as const
          ) as string,
        });
      })
      .then(onClose);
  }, []);

  return (
    <Modal isOpen={open} onClose={onClose} aria-label="Create or update a group">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {group ? t("groups.edit", { groupName: group.name }) : t("groups.add")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InsertGroupForm
            group={group}
            submit={submit}
            defaultName={defaultName}
          />
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              onClick={onClose}
            >
              {t("forms.actions.cancel")}
            </Button>
            <Spacer />
            <Button
              type="submit"
              colorScheme="blue"
              form="insertGroupForm"
              isLoading={loading}
            >
              {t("forms.actions.save")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
